import React, { useState } from "react";
import axios from "axios";
import { CheckCircle2, Sparkles, ShieldCheck, TrendingUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Gate({ onUnlock }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  const update = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Please enter your name.";
    if (!form.email.trim()) er.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      er.email = "Please enter a valid email.";
    if (!form.phone.trim()) er.phone = "Please enter your phone number.";
    else if (form.phone.replace(/\D/g, "").length < 7)
      er.phone = "Please enter a valid phone number.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate() || submitting) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
      });
      setToast({ msg: "You're in — let's run your numbers.", kind: "success" });
      // Persist locally so they don't have to re-enter on refresh
      try {
        localStorage.setItem(
          "fl_lead",
          JSON.stringify({ ...form, id: res.data.id, created_at: res.data.created_at })
        );
      } catch (e) { /* ignore */ }
      setTimeout(() => onUnlock({ ...form, id: res.data.id }), 600);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      setToast({ msg, kind: "error" });
      setSubmitting(false);
    }
  };

  return (
    <div className="fl-gate" data-testid="fl-gate">
      <div className="fl-gate-left fl-fade-in">
        <span className="fl-gate-eyebrow" data-testid="fl-gate-eyebrow">
          Free Financial Fitness Check
        </span>
        <h1 className="fl-gate-title">
          See how a <em>1% shift</em> could change your retirement — without changing your life.
        </h1>
        <p className="fl-gate-sub">
          One quick snapshot of your budget, your future, and your next %. Get a personalized
          report and see how a small shift can create a strong financial foundation.
        </p>
        <ul className="fl-gate-bullets">
          <li>
            <CheckCircle2 size={20} strokeWidth={2.5} />
            <span>A clean view of your Needs / Wants / Future from your real take-home pay.</span>
          </li>
          <li>
            <TrendingUp size={20} strokeWidth={2.5} />
            <span>Your projected retirement value — in today's dollars — based on your current path.</span>
          </li>
          <li>
            <Sparkles size={20} strokeWidth={2.5} />
            <span>The dollar-impact of redirecting just 1–15% more toward your future.</span>
          </li>
          <li>
            <ShieldCheck size={20} strokeWidth={2.5} />
            <span>A downloadable PDF report you can keep — completely free.</span>
          </li>
        </ul>
      </div>

      <form className="fl-gate-card fl-fade-in delay-1" onSubmit={submit} data-testid="fl-gate-form" noValidate>
        <h3>Get free access to the calculator</h3>
        <p className="lead-card-sub">
          Sign up below — see how 1% can change your life.
        </p>

        <div className="fl-form-group">
          <label htmlFor="lead-name">Full name</label>
          <input
            id="lead-name"
            type="text"
            className={`fl-input ${errors.name ? "error" : ""}`}
            placeholder="Jane Smith"
            value={form.name}
            onChange={update("name")}
            autoComplete="name"
            data-testid="fl-input-name"
          />
          {errors.name && <div className="fl-form-error" data-testid="fl-error-name">{errors.name}</div>}
        </div>

        <div className="fl-form-group">
          <label htmlFor="lead-email">Email address</label>
          <input
            id="lead-email"
            type="email"
            className={`fl-input ${errors.email ? "error" : ""}`}
            placeholder="you@example.com"
            value={form.email}
            onChange={update("email")}
            autoComplete="email"
            data-testid="fl-input-email"
          />
          {errors.email && <div className="fl-form-error" data-testid="fl-error-email">{errors.email}</div>}
        </div>

        <div className="fl-form-group">
          <label htmlFor="lead-phone">Phone number</label>
          <input
            id="lead-phone"
            type="tel"
            className={`fl-input ${errors.phone ? "error" : ""}`}
            placeholder="(555) 123-4567"
            value={form.phone}
            onChange={update("phone")}
            autoComplete="tel"
            data-testid="fl-input-phone"
          />
          {errors.phone && <div className="fl-form-error" data-testid="fl-error-phone">{errors.phone}</div>}
        </div>

        <button
          type="submit"
          className="fl-btn"
          disabled={submitting}
          data-testid="fl-submit-lead"
        >
          {submitting ? "Unlocking…" : "Unlock my calculator →"}
        </button>

        <div className="fl-form-trust">
          We respect your privacy. By signing up you agree to receive emails from Finlightenment.
          You can unsubscribe anytime.
        </div>
      </form>

      {toast && (
        <div className={`fl-toast ${toast.kind === "error" ? "error" : ""}`} data-testid="fl-toast">
          {toast.msg}
        </div>
      )}
    </div>
  );
}
